// NCADocumentViewer.js - Final Working Version
import React, { useEffect, useRef, useState, useCallback,useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateNcaCode } from "./redux/CodeBookSlicer";
import closeIcon from "../../common/assets/images/close-icon.png";
import { Table } from "react-bootstrap";
import { Tooltip } from "@mui/material";
import SearchNavBar from "../CodeBook/documentSearch/SearchNavBar"
import { IframeSearchEngine } from "./documentSearch/searchEngines";
import { useDocSearch } from "./documentSearch/useDocSearch";

const NCADocumentViewer = ({query = ""}) => {
  const iframeRef = useRef(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [codeList, setCodeList] = useState([]);
  const { ncaCode } = useSelector((state) => state.codeBook);
  const dispatch = useDispatch();

  // === SEARCH ENGINE SETUP ===
  const engine = useMemo(() => new IframeSearchEngine(), []);
  engine.setRef(iframeRef);

  const search = useDocSearch(engine);

  // Pass search to iframe engine when query changes
  useEffect(() => {
    if (query?.length >= 2) {
      engine.search(query);
    } else {
      engine.clear();
    }
  }, [query, engine]);

  const scrollToNcaCode = useCallback((item) => {
    return new Promise((resolve) => {
      if (!item || !iframeRef.current) {
        resolve(false);
        return;
      }

      try {
        const iframeDoc =
          iframeRef.current.contentDocument ||
          iframeRef.current.contentWindow?.document;

        if (!iframeDoc?.body) {
          resolve(false);
          return;
        }

        const { code, description } = item;

        const normalize = (s) =>
          s
            .replace(/\u00A0/g, " ")
            .replace(/\s+/g, " ")
            .trim();

        const codePattern = normalize(`Ref No: ${code}`);
        const allElements = iframeDoc.body.getElementsByTagName("*");
        let targetElement = null;

        for (let i = 0; i < allElements.length; i++) {
          const element = allElements[i];
          const text = normalize(element.textContent || "");
          const hasCode = text.includes(codePattern);

          const hasDescription = description
            ? text.toLowerCase().includes(description.toLowerCase())
            : true;

          if (hasCode && hasDescription) {
            if (
              !targetElement ||
              element.textContent.length < targetElement.textContent.length
            ) {
              targetElement = element;
            }
          }
        }

        if (targetElement) {
          const iframeWindow = iframeRef.current.contentWindow;

          // Get position relative to iframe document (not viewport)
          let offsetTop = 0;
          let el = targetElement;
          while (el) {
            offsetTop += el.offsetTop || 0;
            el = el.offsetParent;
          }

          // Only scroll INSIDE the iframe — never touches outer page
          iframeWindow.scrollTo({
            top: offsetTop - 100,
            behavior: "smooth",
          });

          // Highlight
          const originalBg = targetElement.style.backgroundColor;
          const originalOutline = targetElement.style.outline;
          targetElement.style.transition = "all 0.3s ease";
          targetElement.style.backgroundColor = "#ffff99";
          targetElement.style.outline = "3px solid #ff6b6b";
          setTimeout(() => {
            targetElement.style.backgroundColor = originalBg;
            targetElement.style.outline = originalOutline;
          }, 3000);

          resolve(true);
        } else {
          resolve(false);
        }
      } catch (error) {
        console.error("Error accessing iframe:", error);
        resolve(false);
      }
    });
  }, []);

  // Handle multiple comma-separated NCS codes
  useEffect(() => {
    if (!ncaCode) return;

    // Handle multiple NCA values
    if (ncaCode.includes(",")) {
      const entries = ncaCode
        // Reference IDs can contain letters and digits (for example TN1690
        // and P652), and the API may include a space after each comma.
        .split(/,\s*(?=[A-Z][A-Z0-9]*-)/)
        .map((item) => item.trim());

      const enriched = entries.map((item, index) => {
        const [code, ...descParts] = item.split("-");

        const description = descParts.join("-").trim();

        return {
          code: code?.trim(),
          description,
          name: description || `Reference ${index + 1}`,
        };
      });

      setCodeList(enriched);
      setOpenModal(true);
      dispatch(updateNcaCode(null));
    } else if (iframeReady) {
      // Single NCA
      const [code, ...descParts] = ncaCode.split("-");

      const item = {
        code: code.trim(),
        description: descParts.join("-").trim(),
      };

      const attemptScroll = async (attempt = 0) => {
        const success = await scrollToNcaCode(item);

        if (!success && attempt < 3) {
          setTimeout(() => attemptScroll(attempt + 1), 500);
        } else if (success) {
          dispatch(updateNcaCode(null));
        }
      };

      setTimeout(() => attemptScroll(), 500);
    }
  }, [ncaCode, iframeReady, scrollToNcaCode, dispatch]);

  const handleModalClick = (item) => {
    setOpenModal(false);
    setTimeout(() => scrollToNcaCode(item), 100);
  };

  return (
    <div style={{ height: "65vh", width: "100%", position: "relative" }}>
            {/* Search Navigation Bar */}
      <SearchNavBar {...search} />
      {openModal && (
        <div className="acs-links-modal-overlay-fade">
          <div className="acs-links-modal-container">
            <div className="acs-links-modal-header">
              <p className="acs-links-modal-header-text">NCA Links</p>
              <p
                className="acs-links-modal-header-img"
                onClick={() => setOpenModal(false)}
              >
                <img src={closeIcon} alt="x" />
              </p>
            </div>
            <div className="acs-links-modal-body">
              <Table style={{ width: "100%" }}>
                <thead className="workflow-him-table-thead">
                  <tr className="workflow-him-table-tr">
                    <th>Name</th>
                    <th>Reference Number</th>
                  </tr>
                </thead>
                <tbody>
                  {codeList.map((item, index) => (
                    <tr
                      key={index}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleModalClick(item)}
                    >
                      <Tooltip title={item.name}>
                        <td style={{ padding: "8px" }}>{item.name}</td>
                      </Tooltip>
                      <td style={{ padding: "8px" }}>{item.code}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      )}

      <iframe
        ref={iframeRef}
        src="/documents/national_coding_advice_Oct.html"
        title="National Coding Advice"
        style={{
          width: "100%",
          height: "calc(100vh - 380px)",
          border: "none",
        }}
        onLoad={() => {
          // console.log("Iframe loaded");
          setIframeReady(true);
          engine.onLoad();
          // If there's a code waiting, scroll to it
          if (ncaCode && !ncaCode.includes(",")) {
            const [code, ...descParts] = ncaCode.split("-");

            setTimeout(() => {
              scrollToNcaCode({
                code: code.trim(),
                description: descParts.join("-").trim(),
              });
            }, 800);
          }
        }}
      />
    </div>
  );
};

export default NCADocumentViewer;
